import React, { useState, useCallback, useMemo } from 'react';
import {
  EDAutoCompleteEdit,
  IAutoCompleteProps,
  IOptionMatch,
} from './EDAutoCompleteEdit';
import { AutocompleteInputChangeReason } from '@mui/material/useAutocomplete';

interface IProps<T>
  extends Omit<IAutoCompleteProps<T>, 'value' | 'onChange' | 'onInputChange'> {
  intervalSeparator: string;
  valueSeparator: string;
  inputValue?: string;
  onInputChange: (value: string) => void;
  getSuggestionValue: (option: T | null) => string;
  /** Letar bara efter ord mellan valueSeperator och struntar i intervalSeperator */
  disableInterval?: boolean;
  wordMaxLength?: number;
}

interface IWord {
  value: number;
  interval: number;
}

const onlyContainsDigitsRegex = new RegExp(/^\d+$/, 'g');

/**
 * A version of the AutoCompleteEdit component that lets the text value be multiple values separated by separator symbols.
 * The shown autocomplete suggestions will depend on which separated part the user is typing in.
 */
function EDAutoCompleteInterval<T>(props: IProps<T>) {
  const { getOptionLabel } = props;
  // Håller koll på vilket ord som editeras just nu
  const [typingValue, setTypingValue] = useState<IWord | null>(null);
  const valueSeparatorRegex = useMemo(
    () => new RegExp(`[${props.valueSeparator}]`, 'g'),
    [props.valueSeparator]
  );
  const intervalSeparatorRegex = useMemo(
    () => new RegExp(`[${props.intervalSeparator}]`, 'g'),
    [props.intervalSeparator]
  );
  const getCurrentWord = (
    newValue: string,
    startIndex: number,
    endIndex: number
  ): IWord | null => {
    if (!newValue) {
      return null;
    }

    return getIWord(newValue, startIndex);
  };

  const getIWord = (text: string, position: number): IWord => {
    const valueSplit = text.split(valueSeparatorRegex);
    const returnWord = { value: 0, interval: 0 };
    for (let i = 0; i < valueSplit.length; i++) {
      const value = valueSplit[i];
      const newPosition = position - value.length - (i === 0 ? 0 : 1);

      if (newPosition <= 0) {
        position -= i === 0 ? 0 : 1;

        if (!props.disableInterval) {
          const intervalSplit = value.split(intervalSeparatorRegex);
          for (let j = 0; j < intervalSplit.length; j++) {
            const interval = intervalSplit[j];
            position -= j === 0 ? interval.length : interval.length + 1;
            if (position <= 0) {
              returnWord.interval = j;
              break;
            }
          }
        }

        returnWord.value = i;
        break;
      }

      position = newPosition;
    }
    return returnWord;
  };

  const onInputChange = (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'clear') {
      setTypingValue(null);
      props.onInputChange('');
    } else if (reason !== 'reset') {
      const target = event.target as HTMLInputElement;
      const start = target.selectionStart ?? 0;
      const end = target.selectionEnd ?? 0;

      const wordMaxLength = props.wordMaxLength;
      // Om det finns ord som bara innehåller siffror och är längre än tillåtna maxlängden
      const someWordExceedsLengthLimit = wordMaxLength
        ? value.split(valueSeparatorRegex).some(value =>
            value.split(intervalSeparatorRegex).some(interval => {
              onlyContainsDigitsRegex.lastIndex = 0;
              return (
                onlyContainsDigitsRegex.test(interval) &&
                interval.length > wordMaxLength
              );
            })
          )
        : false;

      if (!someWordExceedsLengthLimit) {
        setTypingValue(getCurrentWord(value, start, end));
        props.onInputChange(value);
      }
    }
  };

  const getOptionMatchesBase = useCallback(
    (inputValue: string) =>
      (option: T): IOptionMatch<T> => {
        const label: string = getOptionLabel
          ? getOptionLabel(option)
          : typeof option === 'string'
          ? option
          : '';
        const startIndex = label.toLowerCase().indexOf(inputValue);
        return {
          option: option,
          startIndex: startIndex,
        };
      },
    [getOptionLabel]
  );

  const getOptionMatches = useCallback(
    (value: string) => (option: T) => {
      const index = typingValue ?? { value: 0, interval: 0 };
      const subValue = value
        ?.split(valueSeparatorRegex)
        [index.value]?.split(intervalSeparatorRegex)
        [index.interval]?.trim();
      const matchMethod = props.getOptionMatches ?? getOptionMatchesBase;
      return matchMethod(subValue)(option);
    },
    [
      typingValue,
      props.getOptionMatches,
      getOptionMatchesBase,
      intervalSeparatorRegex,
      valueSeparatorRegex,
    ]
  );

  const autoCompleteMatch = useCallback(
    (option: any, inputValue: string): [number, number][] => {
      const subValue = inputValue
        .split(valueSeparatorRegex)
        [typingValue?.value ?? 0].split(intervalSeparatorRegex)
        [typingValue?.interval ?? 0].trim();
      const label = getOptionLabel
        ? getOptionLabel(option)
        : typeof option === 'string'
        ? option
        : '';
      const startIndex = label.toLowerCase().indexOf(subValue);
      if (startIndex === -1) {
        return [];
      }

      return [[startIndex, startIndex + subValue.length]];
    },
    [
      getOptionLabel,
      intervalSeparatorRegex,
      typingValue?.interval,
      typingValue?.value,
      valueSeparatorRegex,
    ]
  );

  const onSuggestionSelected = (option: T | string | null) => {
    if (!option) {
      props.onInputChange('');
      setTypingValue(null);
    } else if (!(typeof option === 'string')) {
      // på en freeSolo komponent med autoselect får vi in att man "selectat" strängen med värdet när man tabbar ur, vi vill bara ha när man faktiskt väljer något
      const splitValues = props.inputValue?.split(valueSeparatorRegex);
      const optionValue = props.getSuggestionValue(option);
      if (typingValue !== null && splitValues) {
        const intervalValues = splitValues[typingValue.value].split(
          intervalSeparatorRegex
        );
        intervalValues.splice(typingValue.interval, 1, optionValue);
        splitValues[typingValue.value] = intervalValues.join(
          props.intervalSeparator
        );
        props.onInputChange(splitValues.join(props.valueSeparator));
      } else {
        props.onInputChange(optionValue);
        setTypingValue({ value: 0, interval: 0 });
      }
    }
  };

  return (
    <EDAutoCompleteEdit<T>
      {...props}
      getOptionMatches={getOptionMatches}
      onChange={onSuggestionSelected}
      onInputChange={onInputChange}
      autoCompleteMatch={autoCompleteMatch}
      freeSolo
    />
  );
}

export default EDAutoCompleteInterval;
